import { useState } from 'react';
import { TrendingUp, Trash2, Copy } from 'lucide-react';

interface AutomationPoint {
  position: number;
  value: number;
}

interface AutomationLaneProps {
  parameter: string;
  color: string;
}

export function AutomationLane({ parameter, color }: AutomationLaneProps) {
  const [points, setPoints] = useState<AutomationPoint[]>([
    { position: 0, value: 0 },
    { position: 25, value: 80 },
    { position: 50, value: 40 },
    { position: 75, value: 90 },
    { position: 100, value: 20 },
  ]);

  const [curve, setCurve] = useState<'linear' | 'smooth' | 'step'>('smooth');

  const addPoint = (position: number) => {
    const newPoint = { position, value: 50 };
    setPoints([...points, newPoint].sort((a, b) => a.position - b.position));
  };

  const removePoint = (index: number) => {
    setPoints(points.filter((_, i) => i !== index));
  };

  return (
    <div className="bg-[#252525] border-b border-[#3a3a3a]">
      <div className="h-8 bg-[#1f1f1f] flex items-center justify-between px-3 border-b border-[#3a3a3a]">
        <div className="flex items-center gap-2">
          <TrendingUp size={12} style={{ color }} />
          <span className="text-xs">{parameter}</span>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={curve}
            onChange={(e) => setCurve(e.target.value as any)}
            className="bg-[#2a2a2a] border border-[#3a3a3a] rounded px-2 py-0.5 text-[10px]"
          >
            <option value="linear">Linear</option>
            <option value="smooth">Smooth</option>
            <option value="step">Step</option>
          </select>
          <button className="p-1 hover:bg-[#2a2a2a] rounded" title="Copy">
            <Copy size={12} />
          </button>
          <button className="p-1 hover:bg-[#2a2a2a] rounded text-red-500" title="Delete">
            <Trash2 size={12} />
          </button>
        </div>
      </div>

      <div className="h-24 relative bg-[#1a1a1a] p-2">
        {/* Grid lines */}
        <div className="absolute inset-2 grid grid-rows-4 grid-cols-8">
          {Array.from({ length: 32 }).map((_, i) => (
            <div key={i} className="border-r border-b border-[#2a2a2a]" />
          ))}
        </div>

        {/* Automation curve */}
        <svg className="absolute inset-2 w-[calc(100%-16px)] h-[calc(100%-16px)]" preserveAspectRatio="none">
          <polyline
            points={points.map(p => `${p.position}%,${100 - p.value}%`).join(' ')}
            fill="none"
            stroke={color}
            strokeWidth="2"
            style={{
              filter: 'drop-shadow(0 0 4px ' + color + ')',
            }}
          />
          
          {/* Control points */}
          {points.map((point, index) => (
            <g key={index}>
              <circle
                cx={`${point.position}%`}
                cy={`${100 - point.value}%`}
                r="4"
                fill={color}
                stroke="#fff"
                strokeWidth="1"
                className="cursor-pointer"
                onClick={() => removePoint(index)}
              />
            </g>
          ))}
        </svg>

        {/* Click to add points */}
        <div
          className="absolute inset-2 cursor-crosshair"
          onClick={(e) => {
            const rect = e.currentTarget.getBoundingClientRect();
            const x = ((e.clientX - rect.left) / rect.width) * 100;
            addPoint(Math.round(x));
          }}
        />
      </div>
    </div>
  );
}
